import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Package, Upload, FileArchive, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { AssetTimelineDialog } from "@/components/AssetTimelineDialog";
import { BulkDisposeDialog } from "@/components/BulkDisposeDialog";

export function Assets() {
  const [assets, setAssets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [includeDisposed, setIncludeDisposed] = useState(false);
  const [form, setForm] = useState({
    name: "",
    acquisition_date: "",
    acquisition_cost: "",
    useful_life_years: "",
    asset_account_id: "",
    depreciation_account_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [postingId, setPostingId] = useState(null);
  const [error, setError] = useState("");
  const [disposeAsset, setDisposeAsset] = useState(null);
  const [disposeForm, setDisposeForm] = useState({
    disposal_date: new Date().toISOString().slice(0, 10),
    disposal_proceeds: "0",
    proceeds_account_id: "",
    gain_loss_account_id: "",
    description: "",
  });
  const [disposing, setDisposing] = useState(false);
  const [timelineAssetId, setTimelineAssetId] = useState(null);
  const [showBulkDispose, setShowBulkDispose] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);

  const companyId = localStorage.getItem("demoCompanyId");

  const loadData = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const [assetRes, accRes] = await Promise.all([
        apiClient.get(`/companies/${companyId}/assets`, {
          params: { include_disposed: includeDisposed },
        }),
        apiClient.get(`/companies/${companyId}/accounts`),
      ]);
      setAssets(assetRes.data || []);
      setAccounts(accRes.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der Anlagen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [includeDisposed]);  // loadData intentionally not in deps (uses companyId from localStorage)

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    if (!form.name || !form.acquisition_date || !form.acquisition_cost || !form.useful_life_years || !form.asset_account_id || !form.depreciation_account_id) {
      setError("Bitte alle Pflichtfelder (Name, Datum, Kosten, Dauer, Konten) ausfüllen.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        acquisition_date: form.acquisition_date,
        acquisition_cost: parseFloat(form.acquisition_cost) || 0,
        useful_life_years: parseInt(form.useful_life_years, 10) || 1,
        asset_account_id: form.asset_account_id,
        depreciation_account_id: form.depreciation_account_id,
      };
      await apiClient.post(`/companies/${companyId}/assets`, payload);
      setForm({
        name: "",
        acquisition_date: "",
        acquisition_cost: "",
        useful_life_years: "",
        asset_account_id: "",
        depreciation_account_id: "",
      });
      await loadData();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Erfassen der Anlage.");
    } finally {
      setSaving(false);
    }
  };

  const handleDepreciate = async (assetId) => {
    setPostingId(assetId);
    setError("");
    try {
      await apiClient.post(`/assets/${assetId}/depreciate`);
      await loadData();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler bei der Abschreibung.");
    } finally {
      setPostingId(null);
    }
  };

  const toggleAutoDepreciation = async (asset) => {
    try {
      await apiClient.post(`/assets/${asset._id}/auto-depreciation`, {
        enabled: !asset.auto_depreciation,
      });
      toast.success(
        !asset.auto_depreciation
          ? `Auto-Abschreibung aktiviert für "${asset.name}"`
          : `Auto-Abschreibung deaktiviert für "${asset.name}"`
      );
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Umschalten fehlgeschlagen.");
    }
  };

  const runAutoDepreciation = async () => {
    if (!companyId) return;
    setAutoRunning(true);
    try {
      const res = await apiClient.post(`/companies/${companyId}/assets/run-auto-depreciation`);
      if (res.data.booked_count > 0) {
        toast.success(
          `${res.data.booked_count} Abschreibung(en) gebucht — CHF ${res.data.total_amount.toFixed(2)} (Monat ${res.data.month})`
        );
      } else {
        toast.info("Keine Abschreibungen fällig (kein Auto-Flag gesetzt oder Monat bereits gebucht).");
      }
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abschreibungslauf fehlgeschlagen.");
    } finally {
      setAutoRunning(false);
    }
  };

  const openDisposeDialog = (asset) => {
    setDisposeAsset(asset);
    setDisposeForm({
      disposal_date: new Date().toISOString().slice(0, 10),
      disposal_proceeds: "0",
      proceeds_account_id: "",
      gain_loss_account_id: "",
      description: `Abgang ${asset.name || ""}`,
    });
    setError("");
  };

  const handleDispose = async (e) => {
    e.preventDefault();
    if (!disposeAsset) return;
    const proceeds = parseFloat(disposeForm.disposal_proceeds || "0") || 0;
    if (proceeds > 0 && !disposeForm.proceeds_account_id) {
      setError("Erlöskonto (Bank/Kasse) ist Pflicht, wenn ein Erlös > 0 gebucht wird.");
      return;
    }
    setDisposing(true);
    setError("");
    try {
      const payload = {
        disposal_date: disposeForm.disposal_date,
        disposal_proceeds: proceeds,
        proceeds_account_id: disposeForm.proceeds_account_id || null,
        gain_loss_account_id: disposeForm.gain_loss_account_id || null,
        description: disposeForm.description || null,
      };
      await apiClient.post(`/assets/${disposeAsset._id}/dispose`, payload);
      setDisposeAsset(null);
      await loadData();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Fehler beim Abgang der Anlage.");
    } finally {
      setDisposing(false);
    }
  };

  const assetAccountOptions = accounts.filter((a) => a.type === "asset");
  const expenseAccountOptions = accounts.filter((a) => a.type === "expense");
  // Für Erlöskonto bei Dispose: nur liquide Konten (1000–1099 = Flüssige Mittel).
  const proceedsAccountOptions = accounts.filter((a) => {
    if (a.type !== "asset") return false;
    const code = String(a.code || "");
    return code.startsWith("10") && code.length >= 4;
  });

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Anlagen</h1>
          <p className="mt-1 text-sm text-slate-500">
            Erfassen Sie Anlagegüter und buchen Sie lineare Abschreibungen (Aufwand an Anlagekonto).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="rounded-full bg-slate-900 text-slate-50"
            onClick={runAutoDepreciation}
            disabled={autoRunning}
            data-testid="assets-run-auto-depreciation-button"
          >
            <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
            {autoRunning ? "Bucht …" : "Monats-Abschreibung buchen"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => setShowBulkDispose(true)}
            data-testid="assets-bulk-dispose-button"
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk-Abgang (CSV)
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            disabled={downloadingZip}
            onClick={async () => {
              if (!companyId) return;
              setDownloadingZip(true);
              try {
                const res = await apiClient.get(`/companies/${companyId}/assets/timelines.zip`, {
                  responseType: "blob",
                });
                const blob = new Blob([res.data], { type: "application/zip" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Anlage_Timelines.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (e) {
                setError(e.response?.data?.detail || "ZIP-Download fehlgeschlagen.");
              } finally {
                setDownloadingZip(false);
              }
            }}
            data-testid="assets-timelines-zip-button"
          >
            <FileArchive className="h-3.5 w-3.5 mr-1.5" />
            {downloadingZip ? "Lade ZIP …" : "Timelines ZIP"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-3xl border border-slate-200 bg-white shadow-sm" data-testid="assets-form-card">
        <form className="space-y-3" onSubmit={handleCreate} data-testid="assets-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="asset-name">Bezeichnung</Label>
              <Input
                id="asset-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="assets-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="asset-date">Anschaffungsdatum</Label>
              <SwissDateField
                id="asset-date"
                
                value={form.acquisition_date}
                onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })}
                data-testid="assets-date-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="asset-cost">Anschaffungskosten</Label>
              <Input
                id="asset-cost"
                type="number"
                value={form.acquisition_cost}
                onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })}
                data-testid="assets-cost-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="asset-life">Nutzungsdauer (Jahre)</Label>
              <Input
                id="asset-life"
                type="number"
                value={form.useful_life_years}
                onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })}
                data-testid="assets-life-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="asset-account">Anlagekonto</Label>
              <Select
                value={form.asset_account_id}
                onValueChange={(value) => setForm({ ...form, asset_account_id: value })}
              >
                <SelectTrigger id="asset-account" data-testid="assets-asset-account-select">
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {assetAccountOptions.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="dep-account">Abschreibungsaufwand</Label>
              <Select
                value={form.depreciation_account_id}
                onValueChange={(value) => setForm({ ...form, depreciation_account_id: value })}
              >
                <SelectTrigger id="dep-account" data-testid="assets-dep-account-select">
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {expenseAccountOptions.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={saving}
              data-testid="assets-save-button"
            >
              {saving ? "Speichern ..." : "Anlage erfassen"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="assets-list-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">Anlagevermögen</h2>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="assets-show-disposed-label">
              <input
                type="checkbox"
                checked={includeDisposed}
                onChange={(e) => setIncludeDisposed(e.target.checked)}
                data-testid="assets-show-disposed-checkbox"
              />
              Inkl. abgegangene Anlagen
            </label>
            {loading && (
              <span className="text-xs text-slate-400" data-testid="assets-loading">
                Lade ...
              </span>
            )}
          </div>
        </div>
        <Table data-testid="assets-table">
          <TableHeader>
            <TableRow>
              <TableHead>Bezeichnung</TableHead>
              <TableHead>Datum</TableHead>
              <TableHead>Kosten</TableHead>
              <TableHead>Kumulierte Abschreibung</TableHead>
              <TableHead>Buchwert</TableHead>
              <TableHead>Status</TableHead>
              <TableHead title="Automatische monatliche Abschreibung">Auto-Abschr.</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assets.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <EmptyState
                    testid="assets-empty-state"
                    icon={<Package className="h-5 w-5" />}
                    title="Noch keine Anlagen"
                    description="Erfassen Sie Anlagegüter und lassen Sie lineare Abschreibungen automatisch verbuchen."
                  />
                </TableCell>
              </TableRow>
            )}
            {assets.map((a) => {
              const isDisposed = a.status === "disposed";
              return (
                <TableRow key={a._id} data-testid={`assets-row-${a._id}`} className={isDisposed ? "bg-slate-50 text-slate-500" : ""}>
                  <TableCell>
                    <button
                      type="button"
                      className="text-left hover:underline decoration-dotted decoration-slate-400 underline-offset-2"
                      onClick={() => setTimelineAssetId(a._id)}
                      data-testid={`assets-timeline-button-${a._id}`}
                      title="Timeline anzeigen"
                    >
                      {a.name}
                    </button>
                    {isDisposed && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">
                        Abgegangen {a.disposal_date || ""}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(a.acquisition_date)}</TableCell>
                  <TableCell>{a.acquisition_cost.toFixed(2)}</TableCell>
                  <TableCell>{(a.accumulated_depreciation || 0).toFixed(2)}</TableCell>
                  <TableCell>{a.book_value.toFixed(2)}</TableCell>
                  <TableCell>
                    {isDisposed
                      ? `Abgegangen (Erlös ${(a.disposal_proceeds || 0).toFixed(2)})`
                      : a.book_value <= 0
                      ? "Voll abgeschrieben"
                      : "Aktiv"}
                  </TableCell>
                  <TableCell>
                    <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!a.auto_depreciation}
                        disabled={isDisposed || a.book_value <= 0}
                        onChange={() => toggleAutoDepreciation(a)}
                        data-testid={`assets-auto-dep-toggle-${a._id}`}
                      />
                      {a.last_auto_depreciation_month && (
                        <span className="text-[10px] text-slate-400" title="Letzter Auto-Lauf">
                          {a.last_auto_depreciation_month}
                        </span>
                      )}
                    </label>
                  </TableCell>
                  <TableCell className="text-right">
                    {!isDisposed && (
                      <div className="inline-flex gap-1.5">
                        {a.book_value > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDepreciate(a._id)}
                            disabled={postingId === a._id}
                            data-testid={`assets-depreciate-button-${a._id}`}
                          >
                            {postingId === a._id ? "Bucht ..." : "Abschreiben"}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-rose-200 text-rose-700 hover:bg-rose-50"
                          onClick={() => openDisposeDialog(a)}
                          data-testid={`assets-dispose-button-${a._id}`}
                        >
                          Abgang
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog
        open={!!disposeAsset}
        onOpenChange={(o) => {
          if (!o) {
            setDisposeAsset(null);
            setDisposeForm({
              disposal_date: new Date().toISOString().slice(0, 10),
              disposal_proceeds: "0",
              proceeds_account_id: "",
              gain_loss_account_id: "",
              description: "",
            });
          }
        }}
      >
        <DialogContent data-testid="assets-dispose-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Anlage-Abgang buchen</DialogTitle>
            <DialogDescription>
              {disposeAsset && (
                <>
                  <span className="font-medium">{disposeAsset.name}</span> — Buchwert:{" "}
                  <span className="font-mono">{(disposeAsset.book_value || 0).toFixed(2)} CHF</span>
                  <br />
                  Differenz zwischen Erlös und Buchwert wird als Gewinn/Verlust gebucht.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-3" onSubmit={handleDispose}>
            <div className="space-y-1">
              <Label htmlFor="dispose-date">Abgangsdatum</Label>
              <SwissDateField
                id="dispose-date"
                
                value={disposeForm.disposal_date}
                onChange={(e) => setDisposeForm({ ...disposeForm, disposal_date: e.target.value })}
                data-testid="assets-dispose-date-input"
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dispose-proceeds">Erlös (CHF) — 0 bei Verschrottung</Label>
              <Input
                id="dispose-proceeds"
                type="number"
                step="0.01"
                min="0"
                value={disposeForm.disposal_proceeds}
                onChange={(e) => setDisposeForm({ ...disposeForm, disposal_proceeds: e.target.value })}
                data-testid="assets-dispose-proceeds-input"
              />
            </div>
            {parseFloat(disposeForm.disposal_proceeds || "0") > 0 && (
              <div className="space-y-1">
                <Label htmlFor="dispose-proceeds-account">Erlöskonto (Bank/Kasse)</Label>
                <Select
                  value={disposeForm.proceeds_account_id}
                  onValueChange={(v) => setDisposeForm({ ...disposeForm, proceeds_account_id: v })}
                >
                  <SelectTrigger id="dispose-proceeds-account" data-testid="assets-dispose-proceeds-account-select">
                    <SelectValue placeholder="Konto wählen (z.B. 1020 Bank)" />
                  </SelectTrigger>
                  <SelectContent>
                    {(proceedsAccountOptions.length > 0 ? proceedsAccountOptions : assetAccountOptions).map((a) => (
                      <SelectItem key={a._id || a.code} value={a.code || a._id}>
                        {a.code} — {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="dispose-gain-account">Gewinn-/Verlust-Konto (optional)</Label>
              <Input
                id="dispose-gain-account"
                placeholder="z.B. 7900 Buchgewinne / 6800 Buchverluste"
                value={disposeForm.gain_loss_account_id}
                onChange={(e) => setDisposeForm({ ...disposeForm, gain_loss_account_id: e.target.value })}
                data-testid="assets-dispose-gain-account-input"
              />
              <p className="text-[11px] text-slate-500">
                Leer → Abschreibungs-Konto der Anlage wird verwendet.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="dispose-description">Beschreibung (Journal)</Label>
              <Input
                id="dispose-description"
                value={disposeForm.description}
                onChange={(e) => setDisposeForm({ ...disposeForm, description: e.target.value })}
                data-testid="assets-dispose-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDisposeAsset(null)} data-testid="assets-dispose-cancel">
                Abbrechen
              </Button>
              <Button type="submit" disabled={disposing} data-testid="assets-dispose-submit">
                {disposing ? "Bucht ..." : "Abgang buchen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AssetTimelineDialog
        open={!!timelineAssetId}
        assetId={timelineAssetId}
        onClose={() => setTimelineAssetId(null)}
      />

      <BulkDisposeDialog
        open={showBulkDispose}
        companyId={companyId}
        onClose={() => setShowBulkDispose(false)}
        onSuccess={loadData}
      />
    </div>
  );
}
